import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, errorText } from '../../api';
import { t } from '../../i18n';
import { fmtDate, initials } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { Sheet } from '../belege/Sheet';
import { useCloseSheet } from './Pages';
import type { LinkItem, NewLink } from './types';

// Mitarbeiter füllt seine Angaben selbst aus: Link verschicken, Liste „Warten auf Angaben“, Link neu senden oder löschen.

// Link an den Mitarbeiter schicken: WhatsApp, Teilen (wo das Handy es anbietet) oder kopieren
export function ShareLink({ link, firm }: { link: NewLink; firm: string }) {
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const text = t(
    'Hallo {name}, bitte tragen Sie hier Ihre Angaben für die Lohnabrechnung bei {firm} ein. Halten Sie dafür bitte Ihren Ausweis bereit: {url}',
    { name: link.firstName, firm, url: link.url },
  );
  const canShare = typeof navigator.share === 'function';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      toast(t('Link kopiert'));
      return;
    } catch {
      // ältere WebViews: über das markierte Feld
    }
    input.current?.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    toast(ok ? t('Link kopiert') : t('Bitte den Link oben markieren und kopieren.'));
  };

  const share = async () => {
    try {
      await navigator.share({ text });
    } catch (err) {
      if ((err as { name?: string }).name !== 'AbortError') toast(t('Teilen hat nicht geklappt. Bitte den Link kopieren.'));
    }
  };

  return (
    <div className="share">
      <p>
        {t(
          'Schicken Sie {name} diesen Link. Darüber kommen die persönlichen Angaben und die Fotos vom Ausweis direkt zu Ihrer Lohnbuchhaltung. Sie bekommen eine Meldung, sobald alles da ist.',
          { name: link.firstName },
        )}
      </p>
      <input
        ref={input}
        readOnly
        value={link.url}
        aria-label={t('Link für den Mitarbeiter')}
        onFocus={(e) => e.target.select()}
      />
      <div className="row">
        <a
          className="btn2 dark"
          href={`https://wa.me/?text=${encodeURIComponent(text)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('Per WhatsApp senden')}
        </a>
        {canShare && (
          <button type="button" className="btn2" onClick={share}>
            {t('Teilen …')}
          </button>
        )}
        <button type="button" className="btn2" onClick={copy}>
          {t('Link kopieren')}
        </button>
      </div>
      <small className="fh">
        {t('Der Link gilt bis {date} und funktioniert nur einmal.', { date: fmtDate(link.expiresAt) })}
      </small>
    </div>
  );
}

// Liste auf der Firmenseite
export function WaitingList({ links }: { links: LinkItem[] }) {
  const navigate = useNavigate();
  return (
    <div className="list">
      {links.map((l) => (
        <button
          key={l.id}
          type="button"
          className="item"
          onClick={() => navigate({ search: `?link=${l.id}` }, { state: { sheet: true } })}
        >
          <span className="ico" aria-hidden="true">
            {initials(l.name)}
          </span>
          <span className="tx">
            <b>{l.name}</b>
            <small>{t('Link geschickt am {date}', { date: fmtDate(l.sentAt) })}</small>
          </span>
          {l.expired ? (
            <span className="badge b-warn">{t('Link abgelaufen')}</span>
          ) : (
            <span className="badge b-none">{t('wartet')}</span>
          )}
          <span className="chev" aria-hidden="true">
            ›
          </span>
        </button>
      ))}
    </div>
  );
}

// Fenster zu einem wartenden Mitarbeiter (?link=<id>)
export function LinkSheetHost({ links, firm }: { links: LinkItem[]; firm: string }) {
  const [params] = useSearchParams();
  const close = useCloseSheet('link');
  const id = params.get('link');
  if (!id) return null;
  return <LinkSheet key={id} l={links.find((x) => x.id === id)} firm={firm} onClose={close} />;
}

function LinkSheet({ l, firm, onClose }: { l: LinkItem | undefined; firm: string; onClose(): void }) {
  const toast = useToast();
  const [fresh, setFresh] = useState<NewLink | null>(null);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!l) {
    return (
      <Sheet title={t('Mitarbeiter')} onClose={onClose}>
        <p className="meta">{t('Diesen Link gibt es nicht mehr. Vielleicht sind die Angaben schon da.')}</p>
      </Sheet>
    );
  }

  const renew = async () => {
    setBusy(true);
    try {
      setFresh(await api.post<NewLink>(`/api/app/lohn-link/${l.id}/renew`));
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.del(`/api/app/lohn-link/${l.id}`);
      toast(t('Gelöscht: {name}', { name: l.name }));
      onClose();
    } catch (err) {
      toast(errorText(err));
      setBusy(false);
    }
  };

  return (
    <Sheet title={l.name} onClose={onClose}>
      {fresh ? (
        <>
          <p className="meta">{t('Neuer Link · der alte funktioniert nicht mehr')}</p>
          <ShareLink link={fresh} firm={firm} />
        </>
      ) : (
        <>
          <p className="meta">
            {l.expired
              ? t('Der Link ist am {date} abgelaufen.', { date: fmtDate(l.expiresAt) })
              : t('Link geschickt am {sent} · gültig bis {until}', { sent: fmtDate(l.sentAt), until: fmtDate(l.expiresAt) })}
          </p>
          <div className="src">
            {t('{name} hat die Angaben noch nicht geschickt.', { name: l.firstName })}{' '}
            {l.expired
              ? t('Schicken Sie einen neuen Link.')
              : t('Ist der Link verloren gegangen, schicken Sie einfach einen neuen.')}
          </div>
          <div className="row">
            <button type="button" className="btn2 dark" onClick={renew} disabled={busy}>
              {t('Link neu senden')}
            </button>
            <button type="button" className="btn2" onClick={() => setAsking(true)} disabled={busy}>
              {t('Löschen')}
            </button>
          </div>
          {asking && (
            <div className="confirm" role="alertdialog" aria-label={t('Link löschen')}>
              {t('{name} wirklich löschen? Der Link funktioniert danach nicht mehr.', { name: l.name })}
              <div className="row">
                <button type="button" className="btn2 del solid" onClick={remove} disabled={busy} autoFocus>
                  {t('Ja, löschen')}
                </button>
                <button type="button" className="btn2" onClick={() => setAsking(false)}>
                  {t('Abbrechen')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </Sheet>
  );
}
